import React, { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { usePagos } from "../state/PagosContext";

// ======= Helpers de fecha (LOCAL) =======
function parseISODate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function money(n) {
  return `$${Number(n).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
}

// Colores consistentes con la app
const COLOR_WORTH = "#0f4c81";   // azul corporativo
const COLOR_JPGM  = "#15803d";   // verde JPGM (tailwind green-700)
const COLOR_TOTAL = "#eab308";   // dorado (tailwind yellow-500)

export default function GraficaProyeccion() {
  const { pagos, getPendienteMonto } = usePagos();

  // mes | quincena
  const [modo, setModo] = useState("quincena");
  // 3,6,9,12 períodos
  const [horizonte, setHorizonte] = useState(6);
  // Mostrar barra total
  const [mostrarTotal, setMostrarTotal] = useState(true);

  const hoy = new Date();

  const data = useMemo(() => {
    const buckets = [];

    const addBucket = (label, start, end) =>
      buckets.push({ label, start, end, worth: 0, jpgm: 0 });

    // Punto de inicio al primer día del mes actual
    const startD = new Date(hoy.getFullYear(), hoy.getMonth(), 1);

    if (modo === "mes") {
      // Meses completos
      for (let i = 0; i < horizonte; i++) {
        const s = new Date(startD.getFullYear(), startD.getMonth() + i, 1);
        const e = new Date(startD.getFullYear(), startD.getMonth() + i + 1, 0, 23, 59, 59);
        const label = s.toLocaleString("es-MX", { month: "short", year: "numeric" });
        addBucket(label, s, e);
      }
    } else {
      // Quincenas (1–15 y 16–31) por cada mes del horizonte
      for (let i = 0; i < horizonte; i++) {
        const s1 = new Date(startD.getFullYear(), startD.getMonth() + i, 1);
        const e1 = new Date(s1.getFullYear(), s1.getMonth(), 15, 23, 59, 59);
        const s2 = new Date(s1.getFullYear(), s1.getMonth(), 16);
        const e2 = new Date(s1.getFullYear(), s1.getMonth() + 1, 0, 23, 59, 59);

        const lab1 = `${s1.toLocaleString("es-MX", { month: "short" })} 1–15`;
        const lab2 = `${s1.toLocaleString("es-MX", { month: "short" })} 16–31`;

        addBucket(lab1, s1, e1);
        addBucket(lab2, s2, e2);
      }
    }

    // Acumular sólo montos PENDIENTES en cada bucket
    for (const p of pagos) {
      const f = parseISODate(p.fechaAjustada || p.fecha);
      const pend = getPendienteMonto(p);
      if (pend <= 0) continue;

      for (const b of buckets) {
        if (f >= b.start && f <= b.end) {
          if (p.responsable === "Worth") b.worth += pend;
          else b.jpgm += pend;
          break;
        }
      }
    }

    return buckets.map((b) => ({
      name: b.label,
      Worth: Math.round(b.worth),
      JPGM: Math.round(b.jpgm),
      Total: Math.round(b.worth + b.jpgm),
    }));
  }, [pagos, getPendienteMonto, modo, horizonte]);

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow p-6 border-t-4 border-worthgold">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h1 className="text-2xl font-extrabold text-worthblue">Proyección de pagos (pendientes)</h1>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              className="border px-2 py-1 rounded"
              value={modo}
              onChange={(e) => setModo(e.target.value)}
            >
              <option value="mes">Por mes</option>
              <option value="quincena">Por quincena</option>
            </select>
            <select
              className="border px-2 py-1 rounded"
              value={horizonte}
              onChange={(e) => setHorizonte(Number(e.target.value))}
            >
              <option value={3}>Próx. 3</option>
              <option value={6}>Próx. 6</option>
              <option value={9}>Próx. 9</option>
              <option value={12}>Próx. 12</option>
            </select>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={mostrarTotal}
                onChange={(e) => setMostrarTotal(e.target.checked)}
              />
              Mostrar barra Total
            </label>
          </div>
        </div>

        <div style={{ width: "100%", height: 360 }}>
          <ResponsiveContainer>
            <BarChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(v) => money(v)} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend />

              {/* Apiladas: Worth + JPGM */}
              <Bar
                dataKey="Worth"
                name="Worth"
                stackId="a"
                fill={COLOR_WORTH}
                stroke={COLOR_WORTH}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="JPGM"
                name="JPGM"
                stackId="a"
                fill={COLOR_JPGM}
                stroke={COLOR_JPGM}
                radius={[4, 4, 0, 0]}
              />

              {/* Barra Total (no apilada, más delgada y semitransparente para no tapar) */}
              {mostrarTotal && (
                <Bar
                  dataKey="Total"
                  name="Total"
                  fill={COLOR_TOTAL}
                  stroke={COLOR_TOTAL}
                  opacity={0.5}
                  barSize={10}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Tabla numérica */}
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b text-worthblue font-bold">
                <th className="py-2">Periodo</th>
                <th>Worth</th>
                <th>JPGM</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.name} className="border-b">
                  <td className="py-1">{r.name}</td>
                  <td>{money(r.Worth)}</td>
                  <td>{money(r.JPGM)}</td>
                  <td className="font-semibold text-worthgold">{money(r.Total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}
